import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import booleanIntersects from "@turf/boolean-intersects";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { feature as asFeature } from "@turf/helpers";
import { pointToLineDistance } from "@turf/point-to-line-distance";
import type { BBox, Feature, LineString, MultiLineString, MultiPolygon, Point, Polygon } from "geojson";
import makeIndex from "geojson-rbush";

import {
  computeStreetParcelFeatures,
  DEFAULT_STREET_PARCEL_CONFIG,
  type StreetGeometry,
  type StreetParcelConfig,
  type StreetParcelFeatures,
} from "./features";
import { polygonParts, toLineStrings } from "./geometry";
import { type CompactModel, predictProbability } from "./model";

export interface ParcelLayerItem {
  id: number | string;
  geometry: Polygon | MultiPolygon;
  addressType: string | null;
}

export interface StreetLayerItem {
  /**
   * The geometry of the street.
   */
  geometry: StreetGeometry;

  /**
   * The OSM "highway" value for this street.
   *
   * "service" streets are generally excluded from consideration.
   */
  highway: string | null;
}

export interface DemandPoint {
  /** A unique identifier of the demand. */
  id: number | string;

  /** The geometry of the demand. */
  geometry: Point;
}

export interface LayerClassificationConfig extends StreetParcelConfig {
  /** A parcel is a candidate only if a non-service street lies within this many degrees of it. */
  gateDistanceDegrees: number;

  /** Candidates whose street-overlap ratio are below this value are ignored. */
  minStreetOverlapRatio: number;

  /** Radius (degrees) for gathering the streets used by centreline length and nearest-street. */
  nearbyRadiusDegrees: number;

  /** `highway` value treated as a service road and excluded from the gate/overlap. */
  serviceHighwayValue: string;

  /**
   * Parcels at/above this probability have their demand membership computed and are considered
   * results. Defaults to the model's own threshold when unset (i.e. only street parcels).
   */
  minProbability?: number;
}

export const DEFAULT_LAYER_CLASSIFICATION_CONFIG: LayerClassificationConfig = {
  ...DEFAULT_STREET_PARCEL_CONFIG,
  gateDistanceDegrees: 0.00015,
  minStreetOverlapRatio: 0.1,
  nearbyRadiusDegrees: 0.001,
  serviceHighwayValue: "service",
};

export interface LayerClassificationResult {
  id: ParcelLayerItem["id"];

  /** Whether this parcel is a street parcel. **/
  isStreetParcel: boolean;

  /** The probability that this parcel is a street parcel, or `null` if it wasn't a candidate. */
  probability: number | null;

  /** The computed model features, or `null` when the parcel was never a candidate. */
  features: StreetParcelFeatures | null;

  /**
   * The IDs of demands that lie inside this parcel.
   *
   * This field is only populated for street parcels.
   */
  demandIds: Array<number | string>;
}

const NOT_A_CANDIDATE = { isStreetParcel: false, probability: null, features: null, demandIds: [] as Array<number | string> };

interface IndexedStreet {
  geometry: StreetGeometry;
  highway: string | null;
}

interface IndexedDemand {
  id: number | string;
}

const parcelBoundaryLines = (parcel: Polygon | MultiPolygon): LineString[] =>
  polygonParts(parcel).flatMap((rings) => rings.map((coordinates) => ({ type: "LineString", coordinates }) as LineString));

/** Return whether the street comes within `gate` degrees of the given parcel. */
function withinGate(street: StreetGeometry, parcel: Polygon | MultiPolygon, gate: number): boolean {
  if (booleanIntersects(asFeature(street), asFeature(parcel))) return true;

  const streetLines = toLineStrings(street);
  const boundary = parcelBoundaryLines(parcel);
  const opts = { method: "planar", units: "degrees" } as const;
  for (const line of streetLines)
    for (const vertex of line.coordinates) for (const edge of boundary) if (pointToLineDistance(vertex, edge, opts) <= gate) return true;
  for (const edge of boundary)
    for (const vertex of edge.coordinates) for (const line of streetLines) if (pointToLineDistance(vertex, line, opts) <= gate) return true;

  return false;
}

/** Spatial index over a street layer. */
type StreetIndex = ReturnType<typeof makeIndex<LineString | MultiLineString, IndexedStreet>>;

/** Return an R-Tree containing all the streets. */
function buildStreetIndex(streets: ReadonlyArray<StreetLayerItem>): StreetIndex {
  const index = makeIndex<LineString | MultiLineString, IndexedStreet>();
  index.load({
    type: "FeatureCollection",
    features: streets.map((s) => asFeature(s.geometry, { geometry: s.geometry, highway: s.highway })),
  });
  return index;
}

/** Spatial index over the demand (address) points. */
type DemandIndex = ReturnType<typeof makeIndex<Point, IndexedDemand>>;

/** Return an R-Tree containing all of the demands. **/
function buildDemandIndex(demands: ReadonlyArray<DemandPoint>): DemandIndex {
  const index = makeIndex<Point, IndexedDemand>();
  index.load({ type: "FeatureCollection", features: demands.map((d) => asFeature(d.geometry, { id: d.id })) });
  return index;
}

/** Return the IDs of all demand points that fall inside the given parcel geometry. **/
function demandsInParcel(parcel: Polygon | MultiPolygon, parcelBbox: BBox, demandIndex: DemandIndex): Array<number | string> {
  const hits = demandIndex.search(bboxPolygon(parcelBbox));
  const ids: Array<number | string> = [];
  for (const f of hits.features as Array<Feature<Point, IndexedDemand>>) {
    if (booleanPointInPolygon(f.geometry, parcel)) ids.push(f.properties.id);
  }
  return ids;
}

function classifyAgainstIndex(
  parcel: ParcelLayerItem,
  streetIndex: StreetIndex,
  demandIndex: DemandIndex,
  model: CompactModel,
  config: LayerClassificationConfig
): LayerClassificationResult {
  const [minX, minY, maxX, maxY] = bbox(asFeature(parcel.geometry));
  const r = config.nearbyRadiusDegrees;
  const hits = streetIndex.search(bboxPolygon([minX - r, minY - r, maxX + r, maxY + r]));
  const nearby = hits.features.map((f: Feature<LineString | MultiLineString, IndexedStreet>) => f.properties);

  const gateStreets = nearby
    .filter((s) => s.highway !== config.serviceHighwayValue && withinGate(s.geometry, parcel.geometry, config.gateDistanceDegrees))
    .map((s) => s.geometry);
  if (gateStreets.length === 0) return { id: parcel.id, ...NOT_A_CANDIDATE };

  const features = computeStreetParcelFeatures(
    { geometry: parcel.geometry, addressType: parcel.addressType, gateStreets, nearbyStreets: nearby.map((s) => s.geometry) },
    config
  );
  if ((features.street_overlap_ratio ?? 0) < config.minStreetOverlapRatio) return { id: parcel.id, ...NOT_A_CANDIDATE };

  const probability = predictProbability(features, model);
  const isStreetParcel = probability >= model.threshold;

  // demand membership only matters for parcels we'd act on (at/above the inclusion floor), so skip the rest.
  const minProbability = config.minProbability ?? model.threshold;
  const demandIds = probability >= minProbability ? demandsInParcel(parcel.geometry, [minX, minY, maxX, maxY], demandIndex) : [];

  return { id: parcel.id, isStreetParcel, probability, features, demandIds };
}

export function classifyParcelLayer(
  parcels: ReadonlyArray<ParcelLayerItem>,
  streets: ReadonlyArray<StreetLayerItem>,
  demands: ReadonlyArray<DemandPoint>,
  model: CompactModel,
  config: LayerClassificationConfig = DEFAULT_LAYER_CLASSIFICATION_CONFIG,
  onProgress?: (completed: number, total: number) => void
): LayerClassificationResult[] {
  const streetIndex = buildStreetIndex(streets);
  const demandIndex = buildDemandIndex(demands);
  const total = parcels.length;
  const reportEvery = Math.max(1, Math.floor(total / 100)); // at most ~100 progress callbacks
  const results: LayerClassificationResult[] = [];
  for (let i = 0; i < total; i++) {
    results.push(classifyAgainstIndex(parcels[i], streetIndex, demandIndex, model, config));
    if (onProgress && (i + 1 === total || (i + 1) % reportEvery === 0)) onProgress(i + 1, total);
  }
  return results;
}
